import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IOrganizationDisplay } from '../types/types.generated';
import { useAllOrganizationDisplaysQuery } from './useRequest.generated';
import { initOrgState, toAllOrgsQueryVars } from '../utils/orgUtils';
import { IDropdown } from '../types/common';
import { mapOrgsToDropdown } from '../domains/Users/utils/userForm';
import { IOrgsFilterState } from '../types/orgFilterState';

interface IGetOrgsResult {
  orgsSelectorLoading: boolean;
  orgsSelectorSearching: boolean;
  orgsSelectorHasError: boolean;
  orgsSelectorIsSuccess: boolean;
  orgOptions: IDropdown[];
}

export interface IOrgsSelectFilterResult {
  orgsSelectorLoading: boolean;
  orgsSelectorSearching: boolean;
  orgsSelectorHasError: boolean;
  orgOptions: IDropdown[];
  orgsSelectorIsSuccess: boolean;
  setOrgsSelectSearchCriteria: (query: string) => void;
}

let debounceTimeout: NodeJS.Timeout;

/**
 * Used to filter the Drop Down lists of Orgs directly from the server instead of
 * letting the Orgs Drop Down Component do it.
 *
 * Reason for this is, we want User's to use search criteria that is not only checked against the Org Name
 * but also in other areas in the Organization like Query Key BANs and such.
 * See https://jira.charter.com/browse/ENTDEL-6569
 */
export const useOrgsSelectFilter = (
  isCharterParentOrg?: boolean
): IOrgsSelectFilterResult => {
  const [searchStr, setSearchStr] = useState('');

  const [result, setResult] = useState<IGetOrgsResult>({
    orgsSelectorLoading: true,
    orgsSelectorSearching: false,
    orgsSelectorHasError: false,
    orgOptions: [],
    orgsSelectorIsSuccess: false,
  });

  const queryClient = useQueryClient();

  const filterState: IOrgsFilterState = {
    ...initOrgState,
    query: searchStr,
  };

  const { isLoading, isFetching, isSuccess, isError, error, data } =
    useAllOrganizationDisplaysQuery(toAllOrgsQueryVars(filterState), {
      refetchOnMount: false,
    });

  useEffect(() => {
    if ((isLoading || isFetching) && searchStr) {
      setResult((state) => ({
        ...state,
        orgsSelectorSearching: true,
      }));
      return;
    }

    if (isLoading) {
      setResult((state) => ({
        ...state,
        isAllOrgsLoading: true,
      }));
      return;
    }

    let allOrgDisplay: IOrganizationDisplay[] =
      data?.allOrganizationDisplays?.organizations || [];
    if (!isCharterParentOrg) {
      allOrgDisplay = allOrgDisplay.filter((org) => org.name !== 'CHARTER');
    }

    let isOrgsSuccess = isSuccess;
    let isOrgsError = isError;

    if (isOrgsError && error && error[0]?.extensions?.code === 'SE.IMP.028') {
      // When no results are found, this is returned as an error but really shouldn't be
      isOrgsSuccess = true;
      isOrgsError = false;
      allOrgDisplay = [];
    }

    setResult({
      orgsSelectorLoading: false,
      orgsSelectorSearching: false,
      orgsSelectorHasError: isOrgsError,
      orgOptions: mapOrgsToDropdown(allOrgDisplay),
      orgsSelectorIsSuccess: isOrgsSuccess,
    });
  }, [isLoading, isFetching, data, isError, isSuccess]);

  const handleSetSearchCriteria = (query: string) => {
    clearTimeout(debounceTimeout);

    // when debounceMs is set, adds delay between keystrokes before firing the upstream search.
    debounceTimeout = setTimeout(() => {
      queryClient
        .cancelQueries({ queryKey: ['allOrganizations'] })
        .catch(console.error);
      queryClient
        .cancelQueries({ queryKey: ['allOrganizationDisplays'] })
        .catch(console.error);

      if (!query) {
        setSearchStr('');
      }

      setSearchStr(query);
    }, 800);
  };

  return {
    ...result,
    setOrgsSelectSearchCriteria: handleSetSearchCriteria,
  };
};
